import { useState } from 'react';
import { createInvitation, type CreatedInvitation, type EmailResult, type GetToken } from '../api';
import { InviteResultList, settleInvite, type InviteFailure, type InviteResult } from './InviteResults';

/**
 * What came of starting a group, shown in the room the group now has.
 *
 * Creating the group and creating N invitations are separate requests and there is no
 * transaction across them (`api.ts`). So the interesting outcome is not the happy one: the
 * group lands and the third invitation does not. This panel is the whole answer to that.
 *
 * Three rules it exists to hold:
 *
 * 1. **The group existing is never hidden behind an error.** A teacher who typed five
 *    addresses and lost one still ends up *inside their new group*, not on an error screen
 *    that leaves them guessing whether to start it again. A second attempt at the whole flow
 *    is how a teacher ends up with two groups of the same name.
 * 2. **Every address that was not invited is named, and retryable on its own.** Not a count,
 *    not "some invitations failed" — the address, because the address is what they have to do
 *    something about, and only the failed ones are retried.
 * 3. **Every address gets the same answer the invite panel gives** (R2), through the same
 *    `InviteResultList`: emailed, or link only and why. A link is presented exactly once, by
 *    `InviteLink`, and only an emailed one is kept behind "Copy link instead".
 *
 * `collab-invite-dialog` is a replay block selector (`analytics/client.ts`). This panel holds
 * several addresses and several live tokens at once, which makes it the single most valuable
 * thing in the product to have accidentally recorded.
 */

export type { InviteFailure } from './InviteResults';

export interface FailedInvite {
  email: string;
  reason: InviteFailure;
}

/** An invitation that landed, and what came of its email. */
export interface StartedInvite {
  invitation: CreatedInvitation;
  emailResult: EmailResult;
}

export interface StartedGroup {
  created: StartedInvite[];
  /** Addresses the Worker answered `already_member` for. Nothing to send, nothing to retry. */
  members: string[];
  failed: FailedInvite[];
}

const FAILURES: readonly InviteFailure[] = ['network', 'refused', 'rate_limited'];
const EMAIL_RESULTS: readonly EmailResult[] = ['sent', 'failed', 'not_configured', 'disabled'];

const isInvitation = (entry: unknown): entry is CreatedInvitation =>
  typeof entry === 'object' && entry !== null
  && typeof (entry as CreatedInvitation).id === 'string'
  && typeof (entry as CreatedInvitation).email === 'string'
  && typeof (entry as CreatedInvitation).link === 'string'
  && typeof (entry as CreatedInvitation).expiresAt === 'string';

/**
 * The same shape, read back out of the router's history state.
 *
 * Validated rather than cast. `history.state` survives a refresh and can be written by anyone
 * with a console, so a component that trusted its shape would render `undefined.map`. The
 * links themselves are the teacher's own — this check is about shape, not about trust.
 *
 * An entry without a known `emailResult` is dropped rather than given one: inventing "sent"
 * would hide a link nobody received, and inventing a reason would state something untrue.
 */
export function asStartedGroup(value: unknown): StartedGroup | null {
  if (typeof value !== 'object' || value === null) return null;
  const started = (value as { started?: unknown }).started;
  if (typeof started !== 'object' || started === null) return null;

  const { created, members, failed } = started as { created?: unknown; members?: unknown; failed?: unknown };
  if (!Array.isArray(created) || !Array.isArray(failed)) return null;

  const links = created.filter((entry): entry is StartedInvite =>
    typeof entry === 'object' && entry !== null
    && isInvitation((entry as StartedInvite).invitation)
    && EMAIL_RESULTS.includes((entry as StartedInvite).emailResult));

  const already = Array.isArray(members)
    ? members.filter((entry): entry is string => typeof entry === 'string')
    : [];

  const refusals = failed.filter((entry): entry is FailedInvite =>
    typeof entry === 'object' && entry !== null
    && typeof (entry as FailedInvite).email === 'string'
    && FAILURES.includes((entry as FailedInvite).reason));

  if (links.length === 0 && already.length === 0 && refusals.length === 0) return null;
  return { created: links, members: already, failed: refusals };
}

export interface StartedGroupPanelProps {
  groupId: string;
  started: StartedGroup;
  getToken: GetToken;
  /** Clears this out of the history entry, so the links stop riding in `history.state`. */
  onDone: () => void;
}

const toRows = (started: StartedGroup): { landed: InviteResult[]; failed: InviteResult[] } => ({
  landed: [
    ...started.created.map(({ invitation, emailResult }): InviteResult => ({
      email: invitation.email, state: 'invited', emailResult, invitation,
    })),
    ...started.members.map((email): InviteResult => ({ email, state: 'already_member' })),
  ],
  failed: started.failed.map(({ email, reason }): InviteResult => ({ email, state: 'failed', reason })),
});

export default function StartedGroupPanel({
  groupId, started, getToken, onDone,
}: StartedGroupPanelProps) {
  const [rows, setRows] = useState(() => toRows(started));
  /** Which address is in flight. One at a time, so the button that is working says so. */
  const [retrying, setRetrying] = useState<string | null>(null);

  async function retry(email: string) {
    setRetrying(email);
    setRows((current) => ({
      ...current,
      failed: current.failed.map((row) => (row.email === email ? { email, state: 'inviting' } : row)),
    }));
    const settled = settleInvite(email, await createInvitation(getToken, groupId, email));
    setRetrying(null);

    // Landed, it moves off the list of what did not happen and onto the list of answers.
    // Failed again, it stays where it was with the new reason.
    setRows((current) => (settled.state === 'failed'
      ? { ...current, failed: current.failed.map((row) => (row.email === email ? settled : row)) }
      : { landed: [...current.landed, settled], failed: current.failed.filter((row) => row.email !== email) }));
  }

  const invited = rows.landed.filter((row) => row.state === 'invited').length;

  return (
    <section className="panel collab-invite-dialog" aria-label="Invitations for your new group">
      <h3>Your new group</h3>

      {rows.failed.length > 0 && (
        <div role="alert">
          <p className="error">
            {rows.failed.length === 1
              ? 'The group was created, but one colleague was not invited.'
              : `The group was created, but ${rows.failed.length} colleagues were not invited.`}
          </p>
          <InviteResultList
            results={rows.failed}
            busy={retrying !== null}
            onRetry={(email) => { void retry(email); }}
          />
        </div>
      )}

      {rows.landed.length > 0 && (
        <>
          {invited > 0 && (
            <p className="hint" role="status">
              {invited === 1 ? 'One invitation is ready.' : `${invited} invitations are ready.`}
            </p>
          )}
          <InviteResultList results={rows.landed} />
        </>
      )}

      <button type="button" onClick={onDone}>Done</button>
    </section>
  );
}
